'use client';
import React, { useRef } from 'react';
import { HDivider } from '@/components/primitives/divider';
import { PlaceDto } from '@/api/place';
import { FeedLinkDto } from '@/api/feed';
// web 전용 DropDown 컴포넌트 (Next 앱의 primitives 사용)
import DropDown, { DropDownItem } from '@/components/primitives/dropdown';
import LinkInput from '@/screen/feed/write/components/link-input';
import LinkList from '@/screen/feed/write/components/link-list';
import StepNavigation from '@/screen/feed/write/components/step-navigation';
import CaptionInput from '@/screen/feed/write/components/caption-input';
import { usePlaceSearch } from '@/screen/feed/write/hooks/usePlaceSearch';
import styles from './styles.module.scss';
import Typography from '@/components/primitives/typography';

interface FeedWriteStep2Props {
  feedId?: string;
  caption: string;
  setCaption: (caption: string) => void;
  links: FeedLinkDto[];
  setLinks: (links: FeedLinkDto[]) => void;
  handleNext: () => void;
  handlePrevious: () => void;
  setBottomFixedViewHeight: (height: number) => void;
  isLoading?: boolean;
  uploadProgress?: number;
  currentFileIndex?: number;
  totalFiles?: number;
  isFeedRegistering?: boolean;
}

/**
 * Second step of feeds writing process - Caption and links (Web version)
 */
const FeedWriteStep2: React.FC<FeedWriteStep2Props> = ({
                                                         feedId,
                                                         caption,
                                                         setCaption,
                                                         links,
                                                         setLinks,
                                                         handleNext,
                                                         handlePrevious,
                                                         setBottomFixedViewHeight,
                                                         isLoading = false,
                                                         uploadProgress = 0,
                                                         currentFileIndex = 0,
                                                         totalFiles = 0,
                                                         isFeedRegistering = false,
                                                       }) => {
  // 장소 검색 훅
  const { searchValue, setSearchValue, searchPlaces, clearSearch } = usePlaceSearch();
  const linkInput = useRef<HTMLInputElement>(null);

  // 연결 삭제
  const handleDeleteLink = (index: number) => {
    const newLinks = [...links];
    newLinks.splice(index, 1);
    setLinks(newLinks);
  };

  // 장소 추가 선택
  const handleAddPlace = (place: PlaceDto) => {
    if (!links.map((a) => a.place?.id).includes(place.id)) {
      setLinks([...links, { place, sortOrder: 0 }]);
      clearSearch();
      linkInput.current?.focus();
    }
  };

  const isCaptionOverLimit = (caption?.length ?? 0) > 1000;

  // 입력 혹은 드롭다운이 포커스되어 있는지 추적
  const [isFieldFocused, setIsFieldFocused] = React.useState(false);

  // 드롭다운 표시 여부: 포커스가 있을 때만 + 검색 결과가 있을 때
  const open = isFieldFocused && !!searchValue && (searchPlaces?.length ?? 0) > 0;

  const getNextLabel = () => {
    if (isFeedRegistering) {
      return '피드 등록 중';
    }
    if (isLoading && totalFiles > 0) {
      return `사진 ${totalFiles} 개 중 ${currentFileIndex} 번 째 업로드 중`;
    }
    if (feedId) {
      return '피드 수정';
    }
    return '피드 등록';
  };

  return (
    <div className={styles.container}>
      <div className={styles.editorGrid}>
        {/* Caption input */}
        <CaptionInput
          className={styles.captionSection}
          caption={caption}
          maxLength={1000}
          onChange={(e) => setCaption(e.target.value)}
        />

        <div className={styles.divider}>
          <HDivider />
        </div>

        {/* Link section */}
        <div className={styles.linkSection}>
          {/* 텍스트 입력과 드롭다운 */}
          <LinkInput
            ref={linkInput}
            value={searchValue}
            onChangeText={setSearchValue}
            editable
            placeholder="피드와 연관된 장소를 검색해서 추가해요!"
            onFocusWithinChange={setIsFieldFocused}
          >
            <DropDown className={styles.placeSearchDropdown} open={open} onClose={() => { /* 닫혀도 입력은 유지 */
            }}>
              {searchPlaces.map((place) => (
                <DropDownItem className={styles.dropdownItem} key={place.id} onSelect={() => handleAddPlace(place)}>
                  <Typography size="large" weight="bold">{place.name}</Typography>
                  <Typography className={styles.address} size="small">
                    {place?.streetAddress?.address || place?.jiBunAddress?.address || ''}
                  </Typography>
                </DropDownItem>
              ))}
              {searchPlaces.length === 0 && (
                <Typography>검색 결과가 없어요</Typography>
              )}
            </DropDown>
          </LinkInput>

          {/* Link list */}
          <LinkList links={links} onDeleteLink={handleDeleteLink} />
        </div>
      </div>

      {/* Navigation buttons */}
      <StepNavigation
        onNext={handleNext}
        onPrevious={handlePrevious}
        nextLabel={getNextLabel()}
        isNextDisabled={(!caption && links.length === 0) || isCaptionOverLimit || isLoading}
        aurora={isLoading}
        onHeightChange={setBottomFixedViewHeight}
      />
    </div>
  );
};

export default FeedWriteStep2;
